/**
 * Three.js Background Component - TRANSCENDENT EDITION
 *
 * 超越的ビジュアルエフェクト:
 * - 量子フィールドの揺らぎ
 * - 意識の海（ニューラルネットワーク）
 * - 幻想の霧（エーテリアルミスト）
 *
 * File structure:
 * - types.ts: Type definitions
 * - effects/quantum-field.ts: Quantum field effect
 * - effects/neural-network.ts: Neural network effect
 * - effects/ethereal-mist.ts: Ethereal mist effect
 */

import React, { useRef, useEffect, useCallback } from 'react';
import * as THREE from 'three';
import { logger } from '@/libs/utils/logger.js';
import { useTokchanStore } from '@/store/tokchanStore.js';
import { getEffectLayerRegistry } from '@/libs/beat-system/index.js';

import type { ThreeBackgroundProps, SystemObject, AnimationContext } from './types.js';
import { PHI } from './types.js';
import { createQuantumField } from './effects/quantum-field.js';
import { createNeuralNetwork } from './effects/neural-network.js';
import { createEtherealMist } from './effects/ethereal-mist.js';

export const ThreeBackground: React.FC<ThreeBackgroundProps> = ({ canvasId = 'three-background' }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const systemsRef = useRef<SystemObject[]>([]);
  const animationIdRef = useRef<number>(0);
  const mouseRef = useRef({ x: 0, y: 0, targetX: 0, targetY: 0, velocity: 0 });
  const timeRef = useRef(0);
  const beatRef = useRef({ intensity: 0, time: 0 });
  const isActiveRef = useRef(true);

  // 🎵 BGM連動: ビート検出コールバック
  const onKickDetected = useCallback((intensity: number, _mood: string, _musicAnalysis: unknown, _layer?: string) => {
    beatRef.current.intensity = Math.min(intensity * 1.5, 1.0);
    beatRef.current.time = timeRef.current;
    logger.debug(`🎵 ThreeBackground: Kick detected (intensity: ${intensity.toFixed(2)})`);
  }, []);

  const onSnareDetected = useCallback((intensity: number, _mood: string, _musicAnalysis: unknown, _layer?: string) => {
    beatRef.current.intensity = Math.max(beatRef.current.intensity, intensity * 0.8);
  }, []);

  const onHihatDetected = useCallback((intensity: number, _mood: string, _musicAnalysis: unknown, _layer?: string) => {
    beatRef.current.intensity = Math.max(beatRef.current.intensity, intensity * 0.3);
  }, []);

  useEffect(() => {
    if (!canvasRef.current) return undefined;

    const canvas = canvasRef.current;

    // Scene setup
    const scene = new THREE.Scene();
    sceneRef.current = scene;

    // Camera setup
    const camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      10000
    );
    camera.position.z = 1000;
    cameraRef.current = camera;

    // Renderer setup
    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({
        canvas,
        alpha: true,
        antialias: true,
        powerPreference: 'high-performance',
        stencil: false
      });
      renderer.setSize(window.innerWidth, window.innerHeight);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      renderer.setClearColor(0x0a1a1a, 1);
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      renderer.toneMappingExposure = 1.5;
      rendererRef.current = renderer;
      logger.info('🌌 Transcendent renderer awakened');
    } catch (error) {
      logger.error('❌ Failed to create WebGL renderer:', error);
      return undefined;
    }

    // Zustand storeに登録
    useTokchanStore.getState().setThreeJS({ scene, camera, renderer });

    // 🎵 BGM連動: EffectLayerRegistryに登録
    const registry = getEffectLayerRegistry();
    const effectLayer = {
      onKickDetected,
      onSnareDetected,
      onHihatDetected,
      activate: () => { isActiveRef.current = true; },
      deactivate: () => { isActiveRef.current = false; },
      isActive: true
    };
    registry.register('background', effectLayer, 'ThreeBackground');
    logger.info('🎵 ThreeBackground registered to EffectLayerRegistry for BGM sync');

    // 創造の順序 - 宇宙の誕生から意識の芽生えまで
    createQuantumField(scene, systemsRef);
    createNeuralNetwork(scene, systemsRef);
    createEtherealMist(scene, systemsRef);

    logger.info('✨ Transcendent visual systems manifested');

    // Mouse tracking with velocity
    let lastMouseX = 0;
    let lastMouseY = 0;
    const handleMouseMove = (event: MouseEvent) => {
      const newX = (event.clientX / window.innerWidth - 0.5) * 2;
      const newY = (event.clientY / window.innerHeight - 0.5) * 2;

      mouseRef.current.velocity = Math.sqrt(
        Math.pow(newX - lastMouseX, 2) + Math.pow(newY - lastMouseY, 2)
      );

      mouseRef.current.targetX = newX;
      mouseRef.current.targetY = newY;
      lastMouseX = newX;
      lastMouseY = newY;
    };

    // クリックでビートを生成
    const handleClick = () => {
      beatRef.current.intensity = 1.0;
      beatRef.current.time = timeRef.current;
    };

    const handleResize = () => {
      if (cameraRef.current && rendererRef.current) {
        cameraRef.current.aspect = window.innerWidth / window.innerHeight;
        cameraRef.current.updateProjectionMatrix();
        rendererRef.current.setSize(window.innerWidth, window.innerHeight);
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('click', handleClick);
    window.addEventListener('resize', handleResize);

    // Animation loop
    const animate = () => {
      animationIdRef.current = requestAnimationFrame(animate);

      const deltaTime = 0.016;
      timeRef.current += deltaTime;
      const time = timeRef.current;

      // Beat decay
      beatRef.current.intensity *= 0.95;

      // Smooth mouse follow with inertia
      mouseRef.current.x += (mouseRef.current.targetX - mouseRef.current.x) * 0.08;
      mouseRef.current.y += (mouseRef.current.targetY - mouseRef.current.y) * 0.08;

      const context: AnimationContext = {
        time,
        mouse: mouseRef.current,
        beat: beatRef.current,
        phi: PHI
      };

      // Update all systems
      systemsRef.current.forEach((system) => {
        if (system.userData.update) {
          system.userData.update(context);
        }
      });

      // Camera
      if (cameraRef.current) {
        const breathe = Math.sin(time * 0.5) * 0.1;
        cameraRef.current.position.x = mouseRef.current.x * 150 + Math.sin(time * 0.3) * 50;
        cameraRef.current.position.y = -mouseRef.current.y * 100 + Math.cos(time * 0.2) * 30;
        cameraRef.current.position.z = 1000 + breathe * 100 + beatRef.current.intensity * 200;
        cameraRef.current.rotation.z = mouseRef.current.x * 0.05 + Math.sin(time * 0.1) * 0.02;
        cameraRef.current.lookAt(0, 0, 0);
      }

      // Render
      if (rendererRef.current && sceneRef.current && cameraRef.current) {
        rendererRef.current.render(sceneRef.current, cameraRef.current);
      }
    };

    animationIdRef.current = requestAnimationFrame(animate);
    logger.info('🌟 Consciousness stream initiated');

    // Cleanup
    return () => {
      // 🎵 BGM連動: EffectLayerRegistryから解除
      try {
        const cleanupRegistry = getEffectLayerRegistry();
        cleanupRegistry.unregister('background');
        logger.info('🎵 ThreeBackground unregistered from EffectLayerRegistry');
      } catch (e) {
        // 無視
      }

      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('click', handleClick);
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationIdRef.current);
      systemsRef.current.forEach(system => {
        if (system instanceof THREE.Points || system instanceof THREE.Mesh || system instanceof THREE.Line) {
          system.geometry.dispose();
          if (Array.isArray(system.material)) {
            system.material.forEach(m => m.dispose());
          } else {
            (system.material as THREE.Material).dispose();
          }
        }
      });
      rendererRef.current?.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onKickDetected, onSnareDetected, onHihatDetected]);

  return (
    <div
      id="three-background-container"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 1,
        pointerEvents: 'none'
      }}>
      <canvas
        ref={canvasRef}
        id={canvasId}
        className="three-background"
        style={{
          width: '100%',
          height: '100%',
          display: 'block'
        }}
      />
    </div>
  );
};

export default ThreeBackground;
